import React from 'react';
import { Button, Container, Grid, IconButton, InputAdornment, Paper, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import './reset-password.form.scss';
import InputForm from '../../commun/input-form';
import { object, ref, string } from 'yup';
import {
    controlLengthMax,
    controlLengthMin,
    controlLowerCase,
    controlNumeric,
    controlSpecific,
    controlUpperCase,
} from '../../../utils/form-validators.utils';
import { PasswordCriterionsDto } from '../../../services/dtos/password-criterions.dto';
import { NewPasswordDto } from '../../../services/dtos/new-password.dto';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';

//Props d'entrée de la fonction ResetPasswordForm
type Props = {
    handlerSubmit: (e: NewPasswordDto) => void;
    handlerOnChangePassword: (password: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    messagesPassword: string[];
    passwordCriterions: PasswordCriterionsDto | null;
    validate: boolean;
};

/**
 * Composant de type Form pour la connexion
 * @param handlerSubmit handler submit sur le formulaire
 * @param handlerOnChangePassword handler sur le changement de mot de passe
 * @param messagesPassword tooltip du mot de passe
 * @param passwordCriterions critère de validation du mot de passe
 * @param validate permet de savoir si le formulaire est envoyé
 * @constructor
 */
function ResetPasswordForm({
    handlerSubmit,
    handlerOnChangePassword,
    messagesPassword,
    passwordCriterions,
    validate,
}: Props) {
    // etat de la visibilité du mot de passe
    const [showPassword, setShowPassword] = React.useState(false);

    // mise à jour de la visibilité du mot de passe
    const handleClickShowPassword = () => setShowPassword(!showPassword);

    // même principe, mais pour l'input de confirmation
    const [showPasswordConfirmation, setShowPasswordConfirmation] = React.useState(false);

    const handleClickShowPasswordConfirmation = () => setShowPasswordConfirmation(!showPasswordConfirmation);

    //Validation schema
    const validationSchema = object({
        //Controle des champs utilisateurs : obligatoire        //Contrôle du champ mot de passe
        //Contrôle : obligatoire / règles en fonction des critères récupérée
        motDePasse: string()
            .trim()
            // Si le mot de passe est vide
            .required('Le mot de passe est obligatoire')
            // Si le mot de passe ne contient pas de majuscule
            .test('upper-case', 'Le mot de passe doit contenir au moins une lettre en majuscules', value =>
                controlUpperCase(value, passwordCriterions?.upperCase)
            )
            // Si le mot de passe ne contient pas de minuscule
            .test('lower-case', 'Le mot de passe doit contenir au moins une lettre en minuscules', value =>
                controlLowerCase(value, passwordCriterions?.lowerCase)
            )
            // Si le mot de passe ne contient pas de chiffre
            .test('number', 'Le mot de passe doit contenir au moins un chiffre', value =>
                controlNumeric(value, passwordCriterions?.number)
            )
            // Si le mot de passe ne contient pas de symbole
            .test('symbole', 'Le mot de passe doit contenir au moins un caractère spécial : !@#%&', value =>
                controlSpecific(value, passwordCriterions?.specialChars)
            )
            // Si le mot de passe ne contient pas assez de caractères
            .test(
                'lengthMin',
                'Le mot de passe doit contenir au moins ' + passwordCriterions?.minLength + ' caractères',
                value => controlLengthMin(value, passwordCriterions?.minLength)
            )
            // Si le mot de passe ne contient trop de caractères
            .test(
                'lengthMax',
                'Le mot de passe avoir au maximum ' + passwordCriterions?.maxLength + ' caractères',
                value => controlLengthMax(value, passwordCriterions?.maxLength)
            ),
        //Controle confirmation de mot de passe
        confirmationMotDePasse: string()
            .trim()
            // Test si les mot de passe correspondent ou pas
            .required('La confirmation du mot de passe est obligatoire')
            .oneOf([ref('motDePasse')], 'Les mots de passe ne correspondent pas'),
    }); //Infos texte pour le champ mot de passe
    const infosPassword = messagesPassword ? (
        <>
            <p>Le mot de passe doit contenir au moins : </p>
            <ul>
                {messagesPassword.map(value => (
                    <li key={value}>{value}</li>
                ))}
            </ul>
        </>
    ) : null;

    //Initialisation des valeurs du formulaire
    const iniValues = {
        motDePasse: '',
        confirmationMotDePasse: '',
    };

    // Création du formulaire
    return (
        // Encapsulation dans un Container
        <Container
            className="containerResetPassword"
            sx={{
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4">
                            Réinitialisation de votre <br /> mot de passe
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                        <Typography variant={'subtitle2'}>* : champs obligatoires</Typography>
                    </Grid>
                </Grid>

                {/*Formulaire*/}
                <Formik initialValues={iniValues} onSubmit={handlerSubmit} validationSchema={validationSchema}>
                    <Form>
                        <Container
                            maxWidth="sm"
                            sx={{
                                display: 'flex',
                                flexFlow: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {/* Le type de l'input sera impacté par l'état du showpassword, ainsi que l'icone de visibilité */}
                            <InputForm
                                required
                                id="motDePasse"
                                name="motDePasse"
                                label="Mot de passe"
                                tooltipMessage={infosPassword}
                                handleChange={handlerOnChangePassword}
                                type={showPassword ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                data-testid="new-eye-password"
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <InputForm
                                required
                                id="confirmationMotDePasse"
                                name="confirmationMotDePasse"
                                label="Confirmation du mot de passe"
                                tooltipMessage={infosPassword}
                                type={showPasswordConfirmation ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                data-testid="new-confirmation-eye-password"
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPasswordConfirmation}
                                                edge="end"
                                            >
                                                {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography variant={'button'}>
                                <Button type="submit" variant="contained" disabled={validate} data-testid={'submit'}>
                                    Valider
                                </Button>
                            </Typography>
                        </Container>
                    </Form>
                </Formik>
            </Paper>
        </Container>
    );
}

export default ResetPasswordForm;
